import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Paper,
} from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  MonetizationOn as MonetizationOnIcon,
  HourglassEmpty as PendingIcon,
} from "@mui/icons-material";
import Grid from "@mui/material/GridLegacy";
import { RootState, AppDispatch } from "../../store";
import {
  fetchRevenueSummary,
  fetchMonthlyRevenue,
  fetchTopClients,
} from "../../store/slices/billingSlice";
import {
  RevenueSummary,
  MonthlyRevenue,
  TopClient,
} from "../../services/billingService";

const Revenues: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { summary, monthlyRevenue, topClients, loading, error } = useSelector(
    (state: RootState) => state.revenue,
  );
  const { user } = useSelector((state: RootState) => state.auth);

  const [period, setPeriod] = useState("2026");

  useEffect(() => {
    if (user?.user_id) {
      dispatch(fetchRevenueSummary({ userId: user.user_id.toString(), year: period }));
      dispatch(fetchMonthlyRevenue({ userId: user.user_id.toString(), year: period }));
      dispatch(fetchTopClients({ userId: user.user_id.toString(), year: period }));
    }
  }, [dispatch, user, period]);


  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <Typography>Loading revenue data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Revenue Summary Card */}
        <Grid xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                  <AccountBalanceIcon />
                </Avatar>
                <Typography variant="h6">Total Revenue</Typography>
              </Box>
              <Typography variant="h4" color="primary.main">
                ${summary?.total_invoiced ? parseFloat(summary.total_invoiced).toFixed(2) : "0.00"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Year to date
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Collection Rate Card */}
        <Grid xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Avatar sx={{ bgcolor: "success.main", mr: 2 }}>
                  <ReceiptIcon />
                </Avatar>
                <Typography variant="h6">Collection Rate</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {summary?.collection_rate}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {summary?.total_collected} of {summary?.total_invoiced} collected
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Pending Amount Card */}
        <Grid xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Avatar sx={{ bgcolor: "warning.main", mr: 2 }}>
                  <PendingIcon />
                </Avatar>
                <Typography variant="h6">Pending Amount</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                ${summary?.total_pending ? parseFloat(summary.total_pending).toFixed(2) : "0.00"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Awaiting payment
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Period Selection */}
        <Grid xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Period
              </Typography>
              <TextField
                select
                fullWidth
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              >
                <MenuItem value="2026">2026</MenuItem>
                <MenuItem value="2025">2025</MenuItem>
                <MenuItem value="2024">2024</MenuItem>
              </TextField>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Monthly Revenue Chart */}
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Monthly Revenue
            </Typography>
            {monthlyRevenue && monthlyRevenue.length > 0 ? (
              <Box sx={{ height: 300, display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                {monthlyRevenue.map((month, index) => (
                  <Box
                    key={month.month}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography variant="caption" sx={{ mb: 1 }}>
                      {new Date(2000, month.month - 1).toLocaleString('default', { month: 'short' })}
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: 200,
                        backgroundColor: 'primary.main',
                        borderRadius: 1,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                        pb: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          position: 'absolute',
                          top: 8,
                        }}
                      >
                        ${parseFloat(month.invoiced).toFixed(0)}
                      </Typography>
                      <Box
                        sx={{
                          width: '100%',
                          height: `${(parseFloat(month.invoiced) / Math.max(...monthlyRevenue.map(m => parseFloat(m.invoiced)))) * 180}px`,
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {month.invoiced !== "0" ? Math.ceil(parseFloat(month.invoiced) / 1000) : 0}k
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  No revenue data available
                </Typography>
              </Box>
            )}
            
            {/* Detailed Table */}
            <Box sx={{ mt: 3 }}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Invoiced</TableCell>
                      <TableCell align="right">Collected</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {monthlyRevenue && monthlyRevenue?.map((month) => (
                      <TableRow key={month.month}>
                        <TableCell>{new Date(2000, month.month - 1).toLocaleString('default', { month: 'long' })}</TableCell>
                        <TableCell align="right">
                          ${month.invoiced ? parseFloat(month.invoiced).toFixed(2) : "0.00"}
                        </TableCell>
                        <TableCell align="right">{month.collected !== "0" ? Math.ceil(parseFloat(month.collected) / 1000) : 0}k collected</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Top Clients */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Top Clients
            </Typography>
            {topClients && topClients.length > 0 ? (
              topClients.map((client, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: 1,
                    borderColor: "grey.200",
                    borderRadius: 2,
                    "&:hover": {
                      borderColor: "primary.main",
                      boxShadow: 1,
                    },
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {client.customer_name}
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      ${client.total_amount ? parseFloat(client.total_amount).toFixed(2) : "0.00"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      {client.bills_count} cases
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Avg: ${client.total_amount && client.bills_count ? (parseFloat(client.total_amount) / client.bills_count).toFixed(2) : "0.00"}
                    </Typography>
                  </Box>
                  <Box sx={{ width: "100%", height: 8, backgroundColor: "grey.200", borderRadius: 1 }}>
                    <Box
                      sx={{
                        width: `${(parseFloat(client.total_amount) / Math.max(...topClients.map(c => parseFloat(c.total_amount)))) * 100}%`,
                        height: "100%",
                        backgroundColor: "primary.main",
                        borderRadius: 1,
                        transition: "width 0.3s ease",
                      }}
                    />
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="body2" color="textSecondary">
                  No client data available
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Box>
  );
};

export default Revenues;
